import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useAuth } from "../../contexts/AuthContext";
import useVehicleSingle from "../../data/useVehicleSingle";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { TextInput } from "../../components/shared/Inputs/TextInput";
import { NumberInput } from "../../components/shared/Inputs/NumberInput";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { CheckboxInput } from "../../components/shared/Inputs/CheckboxInput";
import Card from "../../components/shared/Card/Card";
import KTypeLookupModal from "../../components/shared/KTypeLookupModal";
import { IVehicle } from "../../models/Vehicle";
import UkvdLookup from "./UkvdLookup";

export default function EditVehicle() {
  const { t } = useTranslation("vehicles");
  const { setInfo } = usePageNameContext();
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { vehicle } = useVehicleSingle(vehicleId);
  const { updateDetails } = useVehiclesMutations();
  const { saveHandlers } = useDefaultCRUDHandlers(t("Vehicle"));
  const { claims } = useAuth();
  const [kTypeLookupOpen, setKTypeLookupOpen] = useState(false);

  // Helper function to convert ISO date string to YYYY-MM-DD format for date inputs
  const formatDateForInput = (dateString: string | null | undefined): string | null => {
    if (!dateString) return null;
    try {
      const date = DateTime.fromISO(dateString);
      if (!date.isValid) return null;
      return date.toISODate() || null;
    } catch {
      return null;
    }
  };

  // Transform vehicle data to format dates for HTML date inputs
  const transformVehicleData = (vehicleData: IVehicle) => {
    if (!vehicleData) return {};
    
    return {
      ...vehicleData,
      motExpiryDate: formatDateForInput(vehicleData.motExpiryDate),
      dateFirstRegistered: formatDateForInput(vehicleData.dateFirstRegistered),
      dateOfFirstRegistrationUK: formatDateForInput(vehicleData.dateOfFirstRegistrationUK),
      visibilityDate: formatDateForInput(vehicleData.visibilityDate),
      terminateDate: formatDateForInput(vehicleData.terminateDate),
    };
  };

  const { register, handleSubmit, reset, formState: { errors }, setValue } = useForm<Partial<IVehicle>>({
    defaultValues: vehicle.data?.data ? transformVehicleData(vehicle.data.data) : {},
  });

  // Reset form when vehicle data loads
  useEffect(() => {
    if (vehicle.data?.data) {
      reset(transformVehicleData(vehicle.data.data));
    }
  }, [vehicle.data?.data, reset]);

  useEffect(() => {
    if (!vehicle.data?.data && !vehicle.isLoading) {
      setInfo({
        name: t("Vehicle not found"),
        desc: t("The vehicle you are looking for does not exist"),
      });
      return;
    }

    if (vehicle.data?.data) {
      setInfo({
        name: t("Edit {{vrm}} {{make}} {{model}}", {
          vrm: vehicle.data.data.vrm,
          make: vehicle.data.data.combinedMake,
          model: vehicle.data.data.modelRangeDescription || vehicle.data.data.combinedModel,
        }),
        desc: t("Edit vehicle details"),
      });
    }
  }, [vehicleId, vehicle.isLoading, vehicle.data?.data, t]);

  const onSubmit = (data: Partial<IVehicle>) => {
    if (!vehicle.data?.data || !vehicleId) return;

    updateDetails.mutate(
      {
        vehicleId,
        details: data,
      },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          navigate(`/vehicles/${vehicleId}`);
        },
        onError: () => {
          saveHandlers.onError();
        },
      }
    );
  };

  if (vehicle.isLoading || !vehicle.data?.data) {
    return (
      <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="mt-8">
      <Card title={t("Edit Vehicle Details")} bodyClassName="p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

          <div>
            <p className="text-red-600 font-bold">{t("WARNING: Manually editing a vehicle could lead to incorrect details being passed onto parts and marketplaces!")}</p>
          </div>


          {/* Basic Information Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Basic Information")}</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <TextInput
                label={t("VRM")}
                register={register}
                registerName="vrm"
                disabled={true}
                cols="sm:col-span-1"
              />
              <UkvdLookup displayName={t("Make")} registerName="combinedMake" setValue={setValue} vehiclePackageData={vehicle.data.data.vehiclePackageData}>
                <TextInput
                  label={t("Make")}
                  register={register}
                  registerName="combinedMake"
                  placeholder="FORD"
                  cols="sm:col-span-1 grow"
                />
              </UkvdLookup>
              
              <UkvdLookup displayName={t("Model")} registerName="combinedModel" setValue={setValue} vehiclePackageData={vehicle.data.data.vehiclePackageData}>
                <TextInput
                  label={t("Model")}
                  register={register}
                  registerName="combinedModel"
                  placeholder="FOCUS EDGE TDCI 95"
                  cols="sm:col-span-1 grow"
                />
              </UkvdLookup>
              <TextInput
                label={t("Model Range")}
                register={register}
                registerName="modelRangeDescription"
                placeholder="Focus"
                cols="sm:col-span-1"
              />
              <NumberInput
                label={t("Year of Manufacture")}
                register={register}
                registerName="dvlaYearOfManufacture"
                placeholder="2014"
                classes="sm:col-span-1"
              />
              <TextInput
                label={t("Chassis No (VIN)")}
                register={register}
                registerName="combinedVIN"
                placeholder=""
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Engine Information Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Engine Information")}</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <NumberInput
                label={t("Engine Size (cc)")}
                register={register}
                registerName="combinedEngineCapacity"
                placeholder="1560"
                classes="sm:col-span-1"
              />
              <TextInput
                label={t("Engine Code")}
                register={register}
                registerName="engineModelCode"
                placeholder=""
                cols="sm:col-span-1"
              />
              <TextInput
                label={t("Engine Manufacturer")}
                register={register}
                registerName="engineManufacturer"
                placeholder="Ford"
                cols="sm:col-span-1"
              />
              <NumberInput
                label={t("Number of Cylinders")}
                register={register}
                registerName="numberOfCylinders"
                placeholder="4"
                classes="sm:col-span-1"
              />
              <TextInput
                label={t("Engine Location")}
                register={register}
                registerName="engineLocation"
                placeholder={t("Front")}
                cols="sm:col-span-1"
              />
              <TextInput
                label={t("Aspiration")}
                register={register}
                registerName="aspiration"
                placeholder={t("TURBOCHARGED")}
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Body & Style Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Body & Style")}</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <TextInput
                label={t("Body Style")}
                register={register}
                registerName="bodyStyleDescription"
                placeholder={t("Estate")}
                cols="sm:col-span-1"
              />
              <NumberInput
                label={t("Number of Doors")}
                register={register}
                registerName="numberOfDoors"
                placeholder="5"
                classes="sm:col-span-1"
              />
              <NumberInput
                label={t("Seats")}
                register={register}
                registerName="seats"
                placeholder="5"
                classes="sm:col-span-1"
              />
              <TextInput
                label={t("Vehicle Category")}
                register={register}
                registerName="vehicleCategoryDescription"
                placeholder={t("Car")}
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Fuel & Transmission Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Fuel & Transmission")}</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <TextInput
                label={t("Fuel Type")}
                register={register}
                registerName="combinedFuelType"
                placeholder={t("Diesel")}
                cols="sm:col-span-1"
              />
              <TextInput
                label={t("Fuel Description")}
                register={register}
                registerName="fuelDescription"
                placeholder={t("HEAVY OIL")}
                cols="sm:col-span-1"
              />
              <TextInput
                label={t("Transmission")}
                register={register}
                registerName="combinedTransmission"
                placeholder={t("Manual")}
                cols="sm:col-span-1"
              />
              <NumberInput
                label={t("Forward Gears")}
                register={register}
                registerName="combinedForwardGears"
                placeholder="6"
                classes="sm:col-span-1"
              />
              <TextInput
                label={t("Transmission Code")}
                register={register}
                registerName="vinvisTransmissionCode"
                placeholder=""
                cols="sm:col-span-1"
              />
              <TextInput
                label={t("Drive Type")}
                register={register}
                registerName="driveType"
                placeholder="4x2"
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Colour Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Colour")}</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <TextInput
                label={t("Current Colour")}
                register={register}
                registerName="colourCurrent"
                placeholder={t("SILVER")}
                cols="sm:col-span-1"
              />
              <TextInput
                label={t("Original Colour")}
                register={register}
                registerName="colourOriginal"
                placeholder={t("SILVER")}
                cols="sm:col-span-1"
              />
              <TextInput
                label={t("Colour Code")}
                register={register}
                registerName="vinvisColourCode"
                placeholder="BNXEWWA"
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Technical Specifications Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Technical Specifications")}</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <NumberInput
                label={t("Gross Weight (KG)")}
                register={register}
                registerName="grossWeightKG"
                placeholder="1900"
                classes="sm:col-span-1"
              />
              <NumberInput
                label={t("Kerb Weight Min (KG)")}
                register={register}
                registerName="kerbWeightMin"
                placeholder="1400"
                classes="sm:col-span-1"
              />
              <NumberInput
                label={t("Kerb Weight Max (KG)")}
                register={register}
                registerName="kerbWeightMax"
                placeholder="1400"
                classes="sm:col-span-1"
              />
              <NumberInput
                label={t("Height (MM)")}
                register={register}
                registerName="heightMM"
                placeholder="1500"
                classes="sm:col-span-1"
              />
              <NumberInput
                label={t("Length (MM)")}
                register={register}
                registerName="lengthMM"
                placeholder="4500"
                classes="sm:col-span-1"
              />
              <NumberInput
                label={t("Width (MM)")}
                register={register}
                registerName="widthMM"
                placeholder="2000"
                classes="sm:col-span-1"
              />
            </div>
          </div>

          {/* MOT & Mileage Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t("MOT & Mileage")}</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <NumberInput
                label={t("Mileage (Miles)")}
                register={register}
                registerName="odometerInMiles"
                placeholder="135488"
                classes="sm:col-span-1"
              />
              <TextInput
                label={t("MOT Expiry Date")}
                register={register}
                registerName="motExpiryDate"
                type="date"
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Dates Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Dates")}</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <TextInput
                label={t("Date First Registered")}
                register={register}
                registerName="dateFirstRegistered"
                type="date"
                cols="sm:col-span-1"
              />
              <TextInput
                label={t("Date of First Registration UK")}
                register={register}
                registerName="dateOfFirstRegistrationUK"
                type="date"
                cols="sm:col-span-1"
              />
              <TextInput
                label={t("Visibility Date")}
                register={register}
                registerName="visibilityDate"
                type="date"
                cols="sm:col-span-1"
              />
              <TextInput
                label={t("Terminate Date")}
                register={register}
                registerName="terminateDate"
                type="date"
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Additional Codes Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Additional Codes")}</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <div className="sm:col-span-1">
                <div className="flex items-end gap-2">
                  <NumberInput
                    label={t("K Type")}
                    register={register}
                    registerName="kType"
                    placeholder="8170"
                    classes="min-w-0 flex-1"
                  />
                  <ButtonInput
                    label={t("Lookup")}
                    isSubmit={false}
                    onClick={() => setKTypeLookupOpen(true)}
                    classes="!bg-white !text-gray-700 border !border-gray-300 hover:!bg-gray-50 shrink-0 self-end"
                  />
                </div>
                <KTypeLookupModal
                  open={kTypeLookupOpen}
                  onClose={() => setKTypeLookupOpen(false)}
                  initialMake={vehicle.data?.data?.combinedMake || vehicle.data?.data?.dvlaMake}
                  initialModel={
                    vehicle.data?.data?.modelRangeDescription ||
                    vehicle.data?.data?.combinedModel ||
                    vehicle.data?.data?.dvlaModel
                  }
                  vehicle={vehicle.data?.data}
                  onSelect={(kType) => {
                    setValue("kType", kType, { shouldDirty: true });
                    setKTypeLookupOpen(false);
                  }}
                />
              </div>
              <TextInput
                label={t("Euro Status")}
                register={register}
                registerName="euroStatus"
                placeholder="5b"
                cols="sm:col-span-1"
              />
              <TextInput
                label={t("Country of Origin")}
                register={register}
                registerName="countryOfOrigin"
                placeholder={t("Germany")}
                cols="sm:col-span-1"
              />
            </div>
          </div>

          {/* Purchase VAT Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Purchase VAT")}</h3>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              <CheckboxInput
                label={t("VAT charged on purchase (trade seller)")}
                register={register}
                registerName="applyVAT"
                disabled={!claims?.isAdmin}
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {claims?.isAdmin
                ? t("Changing this updates any linked leads and, when the margin scheme is enabled, re-stamps the vehicle's sales VAT treatment and margin pool entry.")
                : t("Only admins can change the purchase VAT flag.")}
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4 pt-4 border-t">
            <ButtonInput
              label={t("Cancel")}
              isSubmit={false}
              onClick={() => navigate(`/vehicles/${vehicleId}`)}
              classes="px-6"
            />
            <ButtonInput
              label={t("Save Changes")}
              isSubmit={true}
              classes="px-6"
              onClick={() => {}}
            />
          </div>
        </form>
      </Card>
    </div>
  );
}

